import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useSetAtom } from 'jotai';
import { SearchIcon } from '@heroicons/react/outline';
import Loading from './Loading';
import { useImmer } from 'use-immer';
import api from '../api';
import { IResponse, SearchProduct } from '../types';
import { notificationAtom } from '../atoms/system';
import { DebounceInput } from 'react-debounce-input';
import _ from 'lodash';
import { Updater } from 'use-immer';

enum SuggestType {
  none,
  loading,
  result,
}

type Suggest = {
  type: SuggestType;
  data: SearchProduct[];
};

const ProductSearch = ({
  show,
  limit = 10,
  action,
}: {
  show: boolean;
  action: Updater<{
    show: boolean;
    item: SearchProduct;
  }>;
  limit?: number;
}): JSX.Element => {
  const notification = useSetAtom(notificationAtom);
  const [suggestSearch, setSuggestSearch] = useImmer<Suggest>({
    type: SuggestType.none,
    data: [],
  });
  const handleTyping = async (event: any) => {
    try {
      const q = event.target.value;
      if (!_.isString(q)) {
        throw Error('Search value is not string!');
      }
      if (q.length < 1) {
        setSuggestSearch(draft => {
          draft.type = SuggestType.none;
          draft.data = [];
        });
      } else {
        setSuggestSearch(draft => {
          draft.type = SuggestType.loading;
          draft.data = [];
        });
        const result = await api
          .post(`api/search/product?limit=${limit}`, {
            json: {
              q: q,
            },
          })
          .json<IResponse<SearchProduct[]>>();
        setSuggestSearch(draft => {
          draft.type = SuggestType.result;
          draft.data = result.data;
        });
      }
    } catch (e) {
      console.log(e);
      notification({
        show: true,
        type: null,
        title: null,
      });
    }
  };
  const handleItem = (product_id: string, handle: string, title: string) => {
    action(draft => {
      draft.show = false;
      draft.item.title = title;
      draft.item.handle = handle;
      draft.item.product_id = product_id;
    });
    setSuggestSearch({
      type: SuggestType.none,
      data: [],
    });
  };

  return (
    <Transition show={show} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[999]"
        onClose={() => {
          action(draft => {
            draft.show = false;
          });
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-center p-4 pt-20">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-md bg-white shadow-xl transition-all">
                <div className="relative pl-12">
                  <DebounceInput
                    type="text"
                    className="placeholder-gray-400 block w-full px-3 py-4 pl-0 border-none focus:border-none focus:ring-0 focus:outline-0 rounded-md shadow-none sm:text-sm"
                    placeholder="Nhập tên sản phẩm hoặc id để tìm kiếm..."
                    debounceTimeout={500}
                    onChange={handleTyping}
                  />
                  <div className="flex absolute inset-y-0 left-0 items-center justify-center w-12 pointer-events-none">
                    <SearchIcon className="w-5 h-5 text-gray-700" />
                  </div>
                </div>
                <div className="block">
                  {(() => {
                    switch (suggestSearch.type) {
                      case SuggestType.none:
                        return null;
                      case SuggestType.loading:
                        return (
                          <div className="flex justify-center items-center p-6 pt-2">
                            <Loading color="indigo-600" size={6} />
                          </div>
                        );
                      case SuggestType.result:
                        if (suggestSearch.data.length > 0) {
                          return (
                            <div className="block p-2 pt-0 overflow-y-auto max-h-[600px]">
                              {suggestSearch.data.map((v, i) => (
                                <div
                                  key={i}
                                  className="flex flex-col rounded-md p-3 cursor-pointer hover:bg-gray-100"
                                  onClick={event => {
                                    event.preventDefault();
                                    handleItem(v.product_id, v.handle, v.title);
                                  }}
                                >
                                  <h6 className="text-sm text-gray-700">
                                    {v.title}
                                  </h6>
                                  <span className="text-xs text-gray-400">
                                    Product ID: {v.product_id}
                                  </span>
                                </div>
                              ))}
                            </div>
                          );
                        }
                        return (
                          <div className="text-center text-xs p-4 text-gray-700">
                            Không có sản phẩm nào phù hợp
                          </div>
                        );

                      default:
                        return null;
                    }
                  })()}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ProductSearch;
